import type { ListBuilderConfiguration } from '../../types';
import { completedTasksList } from './lists/completed-tasks';
import { defaultTasksAllList } from './lists/default-tasks-all';
import { highPriorityTasksList } from './lists/high-priority-tasks';
import { overdueTasksList } from './lists/overdue-tasks';
import { thisWeekTasksList } from './lists/this-week-tasks';
import { todaysTasksList } from './lists/todays-tasks';
import { unassignedTasksList } from './lists/unassigned-tasks';

/**
 * Task list configuration
 * Includes calendar support and multiple filtered views
 */
export const TASK_LIST_CONFIG: ListBuilderConfiguration = {
	objectType: 'task',
	lists: [
		todaysTasksList,
		overdueTasksList,
		highPriorityTasksList,
		thisWeekTasksList,
		defaultTasksAllList,
		completedTasksList,
		unassignedTasksList,
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'taskKeywords',
			'taskStatus',
			'taskPriority',
			'taskAssignedUser',
			'taskClient',
			'taskLeadOrCustomer',
			'taskDueDateRange',
			'taskExternalCalendars',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/tasks/:id',
			objectLabel: 'Task',
			objectTable: 'task',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'title', fieldLabel: 'Title' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: 'dueDate', fieldLabel: 'Due Date' },
				{ fieldName: 'clientId', fieldLabel: 'Client ID' },
				{ fieldName: 'clientName', fieldLabel: 'Client Name' },
				{ fieldName: 'assignedUserId', fieldLabel: 'Assigned User ID' },
				{ fieldName: 'assignedUserName', fieldLabel: 'Assigned To' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
				{ fieldName: 'completedDate', fieldLabel: 'Completed Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'created-date-desc',
			name: 'Created (Newest First)',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'task',
					direction: 'DESC',
				},
			],
		},
		{
			id: 'created-date-asc',
			name: 'Created (Oldest First)',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'task',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'title-asc',
			name: 'Title (A-Z)',
			fields: [
				{
					fieldName: 'title',
					objectTable: 'task',
					direction: 'ASC',
				},
			],
		},
	],
	calendarConfig: {
		dateField: {
			fieldName: 'dueDate',
			objectTable: 'task',
		},
		timezoneField: 'timezone',
		defaultTimezone: 'America/New_York',
		eventLayout: {
			titleField: {
				fieldName: 'subject',
				objectTable: 'task',
			},
			popoverFields: [
				{
					id: 'client-name',
					label: 'Client',
					displayStyle: 'text',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientDisplayName',
							objectTable: 'task',
						},
					},
				},
				{
					id: 'priority',
					label: 'Priority',
					displayStyle: 'badge',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'priority',
							objectTable: 'task',
						},
					},
				},
				{
					id: 'status',
					label: 'Status',
					displayStyle: 'badge',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'status',
							objectTable: 'task',
						},
					},
				},
				{
					id: 'assigned-user',
					label: 'Assigned To',
					displayStyle: 'text',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'assignedUserName',
							objectTable: 'task',
						},
					},
				},
			],
		},
		weekConfig: {
			startHour: 8,
			endHour: 18,
			slotInterval: 60,
			maxEventsPerSlot: 3,
			weekStartDay: 'sunday',
		},
		monthConfig: {
			maxEventsPerDay: 4,
			showWeekNumbers: false,
			weekStartDay: 'sunday',
		},
		allowDragDrop: true,
		onEventMove: 'handleTaskMove',
		onEventClick: 'handleTaskClick',
		onEventComplete: 'complete',
		onEventEdit: 'edit',
		onSlotSelect: 'handleSlotSelect',
	},
	enableExport: true,
	exportFormats: ['csv', 'excel'],
	searchFields: ['title', 'description', 'clientName'],
	showResetButton: true,
	enableColumnReorder: true,
	enableColumnResize: true,
};
